/* ========== SHARED CHART UTILITIES ==========
 * COLORS palette + shared draw helpers used by every chart type.
 * Each chart-type module imports from here.
 * ============================================== */

import { formatWithExcelFormat, formatNumberCell } from "../utils/dateFormat";

/**
 * Data-label text for one point, formatted like Excel: with the series'
 * number format (e.g. "$1,235", "12%") or General rounding — never a raw
 * float such as 1234.5678901 or 0.30000000000000004.
 */
export function formatPointValue(chartObj, value) {
  var fmt = chartObj && (chartObj.dataLabelFormat || chartObj.valueFormat);
  var out = formatNumberCell(fmt || "General", value);
  return out == null ? "" : String(out);
}

/** Draw a value data label when the chart authored "show value" labels. */
export function drawValueLabel(ctx, chartObj, value, px, py, placement) {
  var dl = chartObj && chartObj.dataLabels;
  if (!dl || !dl.showVal) return;
  ctx.save();
  ctx.fillStyle = '#404040';
  ctx.font = '9px Calibri, Arial';
  if (placement === 'right') { ctx.textAlign = 'left'; ctx.textBaseline = 'middle'; }
  else { ctx.textAlign = 'center'; ctx.textBaseline = 'bottom'; }
  ctx.fillText(formatPointValue(chartObj, value), px, py);
  ctx.restore();
}

export const COLORS = [
  '#4472C4', '#ED7D31', '#A5A5A5', '#FFC000', '#5B9BD5', '#70AD47', '#264478', '#9B57A0',
  '#636363', '#B85450', '#6C8EBF', '#D6B656', '#82B366', '#E07B53', '#9673A6', '#23445D'
];

export var LEGEND_ROW_HEIGHT = 13;

/* ---------------- Value-axis scaling (Excel-like) ----------------
 * Excel does NOT split [dataMin, dataMax] into 5 equal parts (which gave
 * labels like 0, 8.4, 16.8 …). It picks a "nice" major unit (1/2/5 × 10^n)
 * sized to the plot, starts at 0 unless the data sits far from zero, adds
 * ~5% headroom above the max, and formats labels with the axis' number
 * format. Authored settings in the chart XML (fixed min / max / major unit,
 * number format) always win.
 */
function niceStep(raw) {
  if (!(raw > 0) || !Number.isFinite(raw)) return 1;
  var exp = Math.floor(Math.log10(raw));
  var base = Math.pow(10, exp);
  var f = raw / base;
  // Rounded (not ceiled) to the nearest nice unit — Excel favours the
  // finer unit (0–45 step 5 rather than 0–50 step 10).
  var nice = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10;
  return nice * base;
}

function defaultAxisLabel(v, step) {
  var decimals = step > 0 && step < 1 ? Math.min(10, Math.ceil(-Math.log10(step) - 1e-9)) : 0;
  var n = Number(v.toFixed(decimals));
  if (Object.is(n, -0)) n = 0;
  return String(n);
}

/**
 * @param {number} dataMin
 * @param {number} dataMax
 * @param {object} [opts] { axis: chartObj.valAxis, lengthPx, isPercent, forceZero }
 * @returns {{minValue, maxValue, range, numGridLines, step, label:(v)=>string}}
 */
export function niceAxis(dataMin, dataMax, opts) {
  opts = opts || {};
  var axis = opts.axis || {};
  var lengthPx = opts.lengthPx || 200;
  var lo = Number.isFinite(dataMin) ? dataMin : 0;
  var hi = Number.isFinite(dataMax) ? dataMax : 0;
  if (opts.isPercent) { lo = 0; hi = 1; }
  if (lo > hi) { var t = lo; lo = hi; hi = t; }

  // Excel keeps 0 on the axis unless the data sits far from it (all values
  // within ~1/6 of each other), and pads ~5% beyond the data extremes.
  var minAuto, maxAuto;
  if (lo >= 0) {
    minAuto = hi > 0 && (hi - lo) / hi < 1 / 6 ? lo - (hi - lo) / 2 : 0;
    maxAuto = hi;
  } else if (hi <= 0) {
    minAuto = lo;
    maxAuto = (hi - lo) / -lo < 1 / 6 ? hi + (hi - lo) / 2 : 0;
  } else {
    minAuto = lo;
    maxAuto = hi;
  }
  if (opts.forceZero) minAuto = Math.min(0, minAuto);

  var fixedMin = typeof axis.min === "number" ? axis.min : null;
  var fixedMax = typeof axis.max === "number" ? axis.max : null;
  var span = (fixedMax != null ? fixedMax : maxAuto) - (fixedMin != null ? fixedMin : minAuto);
  if (!(span > 0)) span = Math.abs(hi) || 1;

  var targetIntervals = Math.max(4, Math.min(10, Math.round(lengthPx / 30)));
  var step = typeof axis.majorUnit === "number" && axis.majorUnit > 0
    ? axis.majorUnit
    : (opts.isPercent ? 0.1 * Math.max(1, Math.round(10 / targetIntervals)) : niceStep(span / targetIntervals));

  var minValue;
  if (fixedMin != null) minValue = fixedMin;
  else if (minAuto < 0) minValue = Math.floor((minAuto - 0.05 * (maxAuto - minAuto)) / step + 1e-9) * step;
  else minValue = Math.floor(minAuto / step + 1e-9) * step;
  var maxValue;
  if (fixedMax != null) maxValue = fixedMax;
  else if (opts.isPercent) maxValue = 1;
  else {
    var top = maxAuto > 0 ? maxAuto + 0.05 * (maxAuto - minValue) : maxAuto;
    maxValue = Math.ceil(top / step - 1e-9) * step;
    if (maxValue <= minValue) maxValue = minValue + step;
  }
  var range = maxValue - minValue || 1;
  var numGridLines = Math.max(1, Math.min(50, Math.round(range / step)));

  var fmt = axis.formatCode;
  var label = function (v) {
    var out = null;
    if (fmt) out = formatWithExcelFormat(fmt, v);
    if (out == null && opts.isPercent) out = Math.round(v * 100) + "%";
    return out == null ? defaultAxisLabel(v, step) : out;
  };
  return { minValue: minValue, maxValue: maxValue, range: range, numGridLines: numGridLines, step: step, label: label };
}

export function drawAxes(ctx, x, y, width, height) {
  ctx.strokeStyle = '#999';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y + height);
  ctx.lineTo(x + width, y + height);
  ctx.moveTo(x, y);
  ctx.lineTo(x, y + height);
  ctx.stroke();
}

export function drawGridlines(ctx, x, y, width, height, minValue, range, numGridLines, labelFn) {
  numGridLines = numGridLines || 5;
  ctx.strokeStyle = '#E0E0E0';
  ctx.lineWidth = 1;
  for (var i = 0; i <= numGridLines; i++) {
    var gy = y + height - (i / numGridLines) * height;
    ctx.beginPath();
    ctx.moveTo(x, gy);
    ctx.lineTo(x + width, gy);
    ctx.stroke();
    var val = minValue + (i / numGridLines) * range;
    ctx.fillStyle = '#888';
    ctx.font = '9px Arial';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      typeof labelFn === "function" ? labelFn(val) : (val % 1 === 0 ? val.toString() : val.toFixed(1)),
      x - 4, gy
    );
  }
}

var WRAP_LINE_H = 11;

/**
 * Decide how the x-axis category labels must be laid out so they never
 * overlap. Excel's order of preference:
 *   1. flat   — single horizontal line when every label fits its slot
 *   2. wrap   — word-wrap onto up to 3 lines inside the slot (pivot-chart
 *               composite labels like "John Mar QA" do this)
 *   3. rotate — 45° rotation only when even a single word can't fit,
 *               thinning labels out when rotated ones would still collide
 *
 * Returns { mode, labels, lines, rotate, step, band } where band is the
 * vertical space (px) the labels consume below the axis line.
 */
export function xAxisLabelLayout(ctx, cats, numCategories, spacing) {
  var labels = [];
  var maxW = 0;
  ctx.font = '9px Arial';
  for (var i = 0; i < numCategories; i++) {
    var raw = cats && cats[i] != null && cats[i] !== '' ? '' + cats[i] : '' + (i + 1);
    labels.push(raw);
    var w = ctx.measureText(raw).width;
    if (w > maxW) maxW = w;
  }
  var avail = spacing - 4;

  // 1. Flat — everything fits on one line
  if (!(numCategories > 0) || !(spacing > 0) || maxW <= avail) {
    return {
      mode: 'flat', labels: labels,
      lines: labels.map(function (l) { return [l]; }),
      rotate: false, step: 1, band: 15,
    };
  }

  // 2. Wrap — possible when every individual word fits the slot width
  var canWrap = true;
  var wrapped = [];
  var maxLines = 1;
  for (var j = 0; j < numCategories && canWrap; j++) {
    var words = labels[j].split(/\s+/).filter(function (w2) { return w2 !== ''; });
    if (words.length === 0) { wrapped.push(['']); continue; }
    var lines = [];
    var cur = '';
    for (var k = 0; k < words.length; k++) {
      if (ctx.measureText(words[k]).width > avail) { canWrap = false; break; }
      var tryLine = cur ? cur + ' ' + words[k] : words[k];
      if (ctx.measureText(tryLine).width <= avail) {
        cur = tryLine;
      } else {
        lines.push(cur);
        cur = words[k];
      }
    }
    if (!canWrap) break;
    if (cur) lines.push(cur);
    if (lines.length > 3) lines = lines.slice(0, 3);
    wrapped.push(lines);
    if (lines.length > maxLines) maxLines = lines.length;
  }
  if (canWrap) {
    return {
      mode: 'wrap', labels: labels, lines: wrapped,
      rotate: false, step: 1, band: 6 + maxLines * WRAP_LINE_H + 2,
    };
  }

  // 3. Rotate — truncate very long labels so the band stays reasonable
  var maxChars = 14;
  var tLabels = labels.map(function (l) {
    return l.length > maxChars ? l.slice(0, maxChars - 1) + '…' : l;
  });
  var truncW = 0;
  for (var t = 0; t < tLabels.length; t++) {
    var tw = ctx.measureText(tLabels[t]).width;
    if (tw > truncW) truncW = tw;
  }
  return {
    mode: 'rotate', labels: tLabels, lines: null, rotate: true,
    step: spacing >= 11 ? 1 : Math.ceil(11 / Math.max(1, spacing)),
    band: Math.ceil(truncW * 0.71) + 10,
  };
}

/** Truncate a label with an ellipsis so it fits maxW pixels (9px font). */
function fitLabel(ctx, label, maxW) {
  if (ctx.measureText(label).width <= maxW) return label;
  var l = label;
  while (l.length > 1 && ctx.measureText(l + '…').width > maxW) {
    l = l.slice(0, -1);
  }
  return l + '…';
}

export var MULTI_LEVEL_ROW_HEIGHT = 14;

/**
 * Multi-level category axis (Excel multiLvlStrCache): each level renders
 * on its own row below the axis — level 0 (most detailed, e.g. "Sunday")
 * closest to the axis, parent levels ("2020", "Jan") underneath — with
 * light vertical separators between category slots, exactly like Excel's
 * boxed multi-row axis. Labels in higher levels that repeat across
 * consecutive slots are merged and centered over their span.
 * Returns the vertical band (px) consumed.
 */
export function drawMultiLevelXAxisLabels(ctx, x, yBase, levels, numCategories, slotW) {
  var numLevels = levels.length;
  var band = 6 + numLevels * MULTI_LEVEL_ROW_HEIGHT + 2;

  // Vertical slot separators spanning the whole label band
  ctx.strokeStyle = '#D9D9D9';
  ctx.lineWidth = 1;
  for (var k = 0; k <= numCategories; k++) {
    var sx = x + k * slotW;
    ctx.beginPath();
    ctx.moveTo(sx, yBase);
    ctx.lineTo(sx, yBase + band - 2);
    ctx.stroke();
  }

  ctx.font = '9px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (var li = 0; li < numLevels; li++) {
    var rowY = yBase + 6 + li * MULTI_LEVEL_ROW_HEIGHT + MULTI_LEVEL_ROW_HEIGHT / 2;
    var labels = levels[li] || [];
    var i = 0;
    while (i < numCategories) {
      var label = labels[i] != null ? '' + labels[i] : '';
      // Merge consecutive repeats (Excel writes parent labels once per span)
      var j = i;
      while (j + 1 < numCategories && ('' + (labels[j + 1] != null ? labels[j + 1] : '')) === label) j++;
      if (label) {
        var spanW = (j - i + 1) * slotW;
        var cx = x + i * slotW + spanW / 2;
        ctx.fillStyle = '#666';
        ctx.fillText(fitLabel(ctx, label, spanW - 4), cx, rowY);
      }
      i = j + 1;
    }
  }
  return band;
}

/**
 * Draw x-axis labels with automatic 45° rotation + thinning when they
 * would overlap. `centered` places each label mid-slot (column charts);
 * otherwise labels sit at the data point (line charts).
 * When `catLevels` (multi-level categories) is provided, delegates to the
 * Excel-style stacked-row layout instead.
 * Returns the vertical band (px) consumed, so callers can place the
 * legend underneath.
 */
export function drawXAxisLabels(ctx, x, yBase, cats, numCategories, spacing, centered, catLevels) {
  if (!numCategories || numCategories <= 0 || !(spacing > 0)) return 15;
  if (catLevels && catLevels.length > 1) {
    // Multi-level axes are always slot-based: derive slot width from the
    // point spacing when the caller is point-aligned (line charts).
    var slotW = centered ? spacing : (spacing * Math.max(1, numCategories - 1)) / numCategories;
    return drawMultiLevelXAxisLabels(ctx, x, yBase, catLevels, numCategories, slotW);
  }
  var layout = xAxisLabelLayout(ctx, cats, numCategories, spacing);
  ctx.font = '9px Arial';
  for (var i = 0; i < numCategories; i += layout.step) {
    var tickX = centered ? x + i * spacing + spacing / 2 : x + i * spacing;
    ctx.fillStyle = '#666';
    if (layout.rotate) {
      ctx.save();
      ctx.translate(tickX, yBase + 5);
      ctx.rotate(-Math.PI / 4);
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(layout.labels[i], 0, 0);
      ctx.restore();
    } else {
      // flat or wrapped: one or more horizontal lines per category
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      var lines = layout.lines ? layout.lines[i] : [layout.labels[i]];
      for (var li = 0; li < lines.length; li++) {
        ctx.fillText(lines[li], tickX, yBase + 6 + li * 11);
      }
    }
  }
  return layout.band;
}

export function drawCategoryLabels(ctx, x, y, height, cats, numCategories, groupWidth, catLevels) {
  return drawXAxisLabels(ctx, x, y + height, cats, numCategories, groupWidth, true, catLevels);
}

/**
 * Pack legend entries into rows so no row is wider than maxWidth.
 */
export function computeLegendRows(ctx, seriesArr, maxWidth) {
  if (!seriesArr || seriesArr.length === 0) return [];
  ctx.font = '9px Calibri, Arial';
  var rows = [];
  var current = [];
  var currentW = 0;
  seriesArr.forEach(function (s, i) {
    var w = 14 + ctx.measureText(s.name || '').width + 16;
    if (current.length > 0 && currentW + w > maxWidth) {
      rows.push(current);
      current = [];
      currentW = 0;
    }
    current.push({
      name: s.name || '',
      color: s.color || COLORS[i % COLORS.length],
      width: w,
      idx: i,
    });
    currentW += w;
  });
  if (current.length > 0) rows.push(current);
  return rows;
}

// Set by the renderer per chart: true = chart has a legend, false = none,
// null = unknown (fall back to "show for 2+ series").
var LEGEND_MODE = null;
export function setLegendMode(mode) { LEGEND_MODE = mode; }

export function drawLegend(ctx, x, y, width, height, seriesArr, options) {
  if (!seriesArr || seriesArr.length === 0) return;
  if (LEGEND_MODE === false) return;
  // Unknown legend setting: Excel's default hides it for one series.
  if (LEGEND_MODE !== true && seriesArr.length < 2) return;
  var isLine = options && options.lineStyle;
  // Sit below the x-axis label band (rotated labels eat more space).
  var labelBand = (options && options.labelBand) || 15;
  ctx.font = '9px Calibri, Arial';
  ctx.textBaseline = 'middle';

  var rows = computeLegendRows(ctx, seriesArr, width);
  var firstRowY = y + height + labelBand + 7;

  rows.forEach(function (row, rowI) {
    var rowWidth = row.reduce(function (s, it) { return s + it.width; }, 0);
    var legendX = x + (width - rowWidth) / 2;
    var legendY = firstRowY + rowI * LEGEND_ROW_HEIGHT;
    row.forEach(function (item) {
      var color = item.color;
      if (isLine) {
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(legendX, legendY);
        ctx.lineTo(legendX + 10, legendY);
        ctx.stroke();
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(legendX + 5, legendY, 2.5, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillStyle = color;
        ctx.fillRect(legendX, legendY - 5, 10, 10);
      }
      ctx.fillStyle = '#333';
      ctx.textAlign = 'left';
      ctx.fillText(item.name, legendX + 14, legendY);
      legendX += item.width;
    });
  });
}

/**
 * Value-axis scale for a set of series. With `chartObj` the scale follows
 * Excel's auto-scaling and the chart's authored axis options; `lengthPx` is
 * the plot length along the value axis (picks the major unit).
 */
export function getMinMaxRange(seriesArr, chartObj, lengthPx) {
  var allVals = seriesArr.flatMap(function (s) { return s.data; })
    .filter(function (v) { return typeof v === "number" && Number.isFinite(v); });
  var dataMax = allVals.length ? Math.max.apply(null, allVals) : 0;
  var dataMin = allVals.length ? Math.min.apply(null, allVals) : 0;
  return niceAxis(dataMin, dataMax, {
    axis: chartObj && chartObj.valAxis,
    lengthPx: lengthPx,
  });
}

/** Darken (negative percent) or lighten (positive percent) a hex color. */
export function shadeColor(hex, percent) {
  var m = hex.replace('#', '');
  var num = parseInt(m, 16);
  var r = (num >> 16) + Math.round(255 * percent / 100);
  var g = ((num >> 8) & 0xff) + Math.round(255 * percent / 100);
  var b = (num & 0xff) + Math.round(255 * percent / 100);
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}
